import { useOrganizationContact, useNewProspectDrawer, usePipelineState } from '../data/hooks';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useContact } from '@copilot/common/hooks/contact';
import { AutomatedStep } from '../../../contactDrawerV2/types';
import { isNil } from 'lodash';
import { calcAutomatedSteps } from '@copilot/common/pages/inbox/ui/component/automationHelpers';
import { useContactLinkedinProfile } from '@copilot/common/hooks/linkedin';
import { useMessagesForLinkedinProfile } from '@copilot/common/hooks/activity';
import { arrayToMap } from '@copilot/common/utils';
import { useProspectDrawerTracking } from '../tracking';
import { OrmState } from 'redux-orm';
import { useContactConnection } from '@copilot/common/hooks/contactConnection';
import { ActivitySelectors } from '@copilot/common/store/selectors/activity';
import { ContactConnectionSelectors } from '@copilot/common/store/selectors/contactConnection';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { dispatchContactUpdate } from './util';
import { meetingBookedDisabledStyles, meetingBookedStyles } from './styles';
import { useReminders } from '../data';
import moment from 'moment';
import InboxMessageSelectors from '@copilot/common/pages/inbox/data/selector';

type UseContactDrawerStateProps = {
    initContactId: string;
    initThreadIdx: number;
    initThreadId: string;
    initMemberId: string;
};

/**
 * Externalized state for the contact drawer, removed from the contact drawer proper to improve readability
 * @param param0
 * @returns
 */
export function useContactDrawerState({
    initContactId,
    initThreadIdx,
    initThreadId,
    initMemberId,
}: UseContactDrawerStateProps) {
    const [contactId, setContactId] = useState(initContactId);
    useEffect(() => {
        useContact(contactId);
    }, [contactId]);

    const [threadIdx, setThreadIdx] = useState(initThreadIdx);
    const [threadId, setThreadId] = useState(initThreadId);
    const [visitedThreadIds, setVisitedThreadIds] = useState(new Set([initThreadId]));
    const [hasSentMessage, setHasSentMessage] = useState(false);
    const messages = useSelector(InboxMessageSelectors);
    const [automatedSteps, setAutomatedSteps] = useState<AutomatedStep[] | undefined>(
        isNil(initThreadId)
            ? undefined
            : calcAutomatedSteps(messages?.data?.[Number(initThreadIdx)]?.nextNodes)
    );
    const [selectedMemberId, setSelectedMemberId] = useState<string>(initMemberId);
    const numThreads = messages.data?.length ?? 1;

    const orgMember = useSelector((state) =>
        OrganizationMemberSelectors.getOrgMember(state, selectedMemberId)
    );
    const contactConnection = useSelector((state: { entities: OrmState<any> }) =>
        ContactConnectionSelectors.getContactConnectionById(state, contactId)
    );

    const activeAdmin = useSelector(OrganizationMemberSelectors.getAdminMember);
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const isOrgAdmin = activeMember?.isOrgAdmin === true || activeMember?.isSysAdmin === true;
    const isOwner = activeMember?.id == selectedMemberId || isOrgAdmin || activeAdmin != null;

    const dispatch = useDispatch();

    const orgId = orgMember ? orgMember.organizationId : '';

    useContactConnection(orgId, contactId);
    const updateProspectDrawerTracking = useProspectDrawerTracking('Prospect Drawer');
    const isNewProspectDrawerEnabled = useNewProspectDrawer();

    const contactConnectionMapping = useMemo(
        () =>
            contactConnection?.connections &&
            arrayToMap(contactConnection.connections, (connection) => connection.orgMemberId),
        [contactConnection?.connections]
    );

    const selectedContactConnection = useMemo(
        () => contactConnectionMapping?.get(selectedMemberId),
        [contactConnectionMapping, selectedMemberId]
    );
    const contactProfile = useContactLinkedinProfile(orgId, contactId);
    const [contactTags, setContactTags] = useState<string[]>([]);

    const [conversationId, isMessagesLoading] = useMessagesForLinkedinProfile(
        selectedContactConnection?.orgMemberProfileId,
        selectedContactConnection?.profileId
    );
    const conversation = useSelector((state: { entities: OrmState<any> }) =>
        ActivitySelectors.getConversation(state, conversationId)
    );

    const orgContactData = useOrganizationContact(contactId, (updated) => {
        dispatchContactUpdate({
            id: updated.id,
            company: updated.company,
            email: updated.email,
            phoneNumber: updated.phoneNumber,
            position: updated.title,
        });
    });

    const reminders = useReminders(
        conversationId,
        selectedContactConnection?.id ?? '',
        updateProspectDrawerTracking
    );

    const flagStates = isNewProspectDrawerEnabled
        ? meetingBookedStyles
        : meetingBookedDisabledStyles;

    const reminderDate = conversation?.snoozedDateTime
        ? moment(conversation.snoozedDateTime)
        : undefined;

    const reminderProps = {
        reminderDate,
        useReminderFunctions: reminders,
    };

    const pipelineStates = usePipelineState(selectedContactConnection, conversation);

    const selectedCampaignConnection = orgContactData.getCampaignConnection(selectedMemberId);

    useEffect(() => {
        if (selectedContactConnection)
            setContactTags(selectedContactConnection.tags.map((t) => t.name));
    }, [selectedContactConnection]);

    return {
        contactId,
        threadIdx,
        threadId,
        visitedThreadIds,
        hasSentMessage,
        messages,
        automatedSteps,
        selectedMemberId,
        numThreads,
        orgMember,
        contactConnection,
        activeAdmin,
        activeMember,
        isOrgAdmin,
        isOwner,
        orgId,
        useContactConnection,
        updateProspectDrawerTracking,
        isNewProspectDrawerEnabled,
        contactConnectionMapping,
        selectedContactConnection,
        contactProfile,
        contactTags,
        conversation,
        isMessagesLoading,
        orgContactData,
        flagStates,
        reminderProps,
        pipelineStates,
        setContactId,
        setThreadIdx,
        setThreadId,
        setVisitedThreadIds,
        setHasSentMessage,
        setAutomatedSteps,
        setSelectedMemberId,
        dispatch,
        reminders,
        selectedCampaignConnection,
        setContactTags,
    };
}
